import React, { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Heart, ShoppingCart } from "lucide-react";
import { getAllProducts } from "../api/productApi";
import { useAuth } from "../context/AuthContext";
import config from "../config";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";

const ProductListing = () => {
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const { t, i18n } = useTranslation();
  const { selectedBranch } = useAuth();
  const [products, setProducts] = useState([]);
  const [searchParams] = useSearchParams(); // Access query parameters
  const categoryId = searchParams.get("id"); //
  const [sortOption, setSortOption] = useState("");
  const [sortedProducts, setSortedProducts] = useState([]);

  // Add useEffect to handle sorting when products or sort option changes
  useEffect(() => {
    if (!products) return;

    let sorted = [...products];

    switch (sortOption) {
      case "price-asc":
        sorted.sort((a, b) => a.default_price - b.default_price);
        break;
      case "price-desc":
        sorted.sort((a, b) => b.default_price - a.default_price);
        break;
      case "newest":
        sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      default:
        // Keep original order
        sorted = products;
    }

    setSortedProducts(sorted);
  }, [products, sortOption]);

  useEffect(() => {
    async function getProducts() {
      const filters = {};
      if (categoryId) {
        filters.category_id = categoryId; // Add only if category_id exists
      }
      if (selectedBranch?.branch_id) {
        filters.branch_id = selectedBranch.branch_id; // Add only if branch_id exists
      }
      const data = await getAllProducts(filters);
      setProducts(data);
    }
    getProducts();
  }, [categoryId, selectedBranch?.branch_id]);
const currentCategoryName = products[0]?.category?.[i18n.language === 'ar' ? 'ar_name' : 'en_name'];
  return (



    <div className="container mx-auto px-4 py-8">
          <Helmet>
 <title>{currentCategoryName ? `${currentCategoryName} | Delire` : 'Our Products | Delire'}</title>
  <meta name="description" content={t("Shop a wide range of curated perfumes from Delire’s exclusive collection.")} />

  {/* Open Graph */}
  <meta property="og:title" content={t("Explore Perfume Collection")} />
  <meta property="og:description" content={t("Browse our luxury scents crafted for all occasions. Discover elegance.")} />
  <meta property="og:type" content="website" />
  <meta property="og:url" content={window.location.href} />

  {/* Use first available image as preview */}
  {products[0]?.productImages?.[0]?.image_url && (
    <meta
      property="og:image"
      content={`${config.apiUrl}${products[0].productImages[0].image_url}`}
    />
  )}

  {/* Optional: JSON-LD for Product Listing Page */}
  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Delire Product Listing",
      "itemListElement": products.slice(0, 10).map((p, index) => ({
        "@type": "ListItem",
        position: index + 1,
        url: `${window.location.origin}/product/${p.product_id}/${encodeURIComponent(p.name)}`
      }))
    })}
  </script>
</Helmet>
      {/* Filters and Sorting */}
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold">{t('OurProducts')}</h1>
        <div className="flex space-x-4">
          <select
            className="border rounded px-2 py-1"
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value)}
          >
            <option value="">{t('Sortby')}</option>
            <option value="price-asc">{t('Pricelh')}</option>
            <option value="price-desc">{t('Pricehl')}</option>
            <option value="newest">{t('Newest')}</option>
          </select>
        </div>
      </div>

      {/* Product Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {sortedProducts.map((product) => {
          const inventory = product.storeInventory?.find(
            (inv) => inv.branch_id === selectedBranch?.branch_id
          );
          const price = inventory ? inventory.price : product.default_price;
          const currency = inventory
            ? inventory.currency
            : product.default_currency;
          const deliveryTime = selectedBranch
            ? inventory && inventory.quantity > 0
              ? `${selectedBranch?.delivery_time} days`
              : `${selectedBranch?.delivery_time_out_stock} days`
            : t("SelectCountry");
          return (
            <div
              key={product.product_id}
              className="relative group"
              onMouseEnter={() => setHoveredProduct(product.product_id)}
              onMouseLeave={() => setHoveredProduct(null)}
            >
              {/* Product Image */}
              <div className="flex justify-center">
                <Link
                  to={`/product/${product.product_id}/${encodeURIComponent(
                    product.name
                  )}`}
                  className="block relative rounded-lg aspect-square w-full max-w-[200px] overflow-hidden"
                >
                  <img
                    src={
                      `${config.apiUrl}${product.productImages[0]?.image_url}` ||
                      "/placeholder.png"
                    }
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
              {product.status ==="inactive" && (
    <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-10 rounded-lg">
      <span className="text-white text-2xl md:text-3xl font-bold tracking-widest">
        {t("SoldOut") || "SOLD OUT"}
      </span>
    </div>
  )}
                  {/* Quick Actions Overlay */}
                  {hoveredProduct === product.product_id && (
                    <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center space-x-4 transition-all duration-300">
                      <button
                        className="bg-white p-2 rounded-full hover:bg-gray-100 transition"
                        aria-label="Add to Wishlist"
                      >
                        <Heart size={20} />
                      </button>
                      <button
                        className="bg-white p-2 rounded-full hover:bg-gray-100 transition"
                        aria-label="Add to Cart"
                      >
                        <ShoppingCart size={20} />
                      </button>
                    </div>
                  )}
                </Link>
              </div>

              {/* Product Details */}
              <div className="mt-4">
                <div className="flex justify-between items-center">
                  <Link
                    to={`/product/${product.product_id}/${encodeURIComponent(
                      product.name
                    )}`}
                    className="font-medium hover:text-blue-600 truncate"
                  >
                    {i18n.dir() === "rtl" ? product.ar_name : product.name}
                  </Link>
                  <span className="font-bold">
                    {price.toFixed(2)} {currency}
                  </span>
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {t('Delivery')}: {deliveryTime}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {/* <div className="flex justify-center mt-12">
        <div className="inline-flex items-center space-x-2">
          <button className="px-4 py-2 border rounded hover:bg-gray-100">
            Previous
          </button>
          <span>Page 1 of 5</span>
          <button className="px-4 py-2 border rounded hover:bg-gray-100">
            Next
          </button>
        </div>
      </div> */}
    </div>
  );
};

export default ProductListing;
